import {useMemo} from 'react';

import {addQueryToPath} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {CompanyModel} from '../../models/api/companyModel';
import {
  ApiTeammates,
  ApiTeammatesSearch,
  TeammateModel,
  TeammatesSearch,
} from '../../models/api/teammateModel';
import {makeResourceListPaginationIds} from '../helpers/resourceListPagination/resourceListPaginationIds';
import {PaginationParameter} from '../helpers/resourceListPagination/resourceListPaginationOffset';
import {makeResourceListPaginationSearchOffset} from '../helpers/resourceListPagination/resourceListPaginationSearchOffset';
import {useResourceList, useResourceListSource} from '../helpers/resourceTableHooks';
import {UseSettingsQueryOptions} from '../helpers/useSettingsQuery';
import {useQueryDebounce} from './fetcherHelpers';

/*
 * Fetchers.
 */

export function useFetchTeammates(query: string) {
  const debouncedQuery = useQueryDebounce(query);
  const teammatesPagination = useMakeTeammateSearchPagination(debouncedQuery);

  const {items, loadMore, moreStatus} = useResourceList(
    ['teammates', {query: debouncedQuery}],
    teammatesPagination,
  );
  return {teammates: items, loadMore, moreStatus};
}

export function useTeammatesSource(query: string) {
  const debouncedQuery = useQueryDebounce(query);
  const teammatesPagination = useMakeTeammateSearchPagination(debouncedQuery);

  return useResourceListSource(['teammates', {query: debouncedQuery}], teammatesPagination);
}

export function useFetchTeammateIds(teammateIds: ReadonlyArray<number>, options?: UseSettingsQueryOptions) {
  const teammatePageinationId = useMemo(
    () =>
      makeResourceListPaginationIds<TeammateModel>({
        apiType: ApiTeammates,
        ids: teammateIds,
        makeUrl: (ids, company) => addQueryToPath(`${company.url}/team`, {ids: ids.join(',')}),
      }),
    [teammateIds],
  );

  const {items, loadMore, moreStatus} = useResourceList(
    ['teammates', {ids: teammateIds}],
    teammatePageinationId,
    options,
  );

  return {teammates: items, loadMore, moreStatus};
}

/*
 * Helpers.
 */

export function buildTeammatesSearchUrl(
  company: CompanyModel,
  query: string,
  pagination: PaginationParameter,
) {
  const path = `${company.url}/search_teammate/${encodeURIComponent(query.toLocaleLowerCase())}`;

  return addQueryToPath(path, pagination);
}

function useMakeTeammateSearchPagination(debouncedQuery: string) {
  return useMemo(
    () =>
      makeResourceListPaginationSearchOffset<TeammateModel, TeammatesSearch>({
        apiType: ApiTeammatesSearch,
        makeSearchUrl: (page, company) => buildTeammatesSearchUrl(company, debouncedQuery, page),
      }),
    [debouncedQuery],
  );
}
